import type { Argument } from "classnames";

import {
  type CSSAnchor,
  type CSSAnchorModifier,
  type CSSAnchorModifierValues,
} from "@/react/helpers/cssAnchors";
import { classNames } from "@/react/helpers/twMergeWithCN";

interface CSSAnchorProps {
  anchor: CSSAnchor;
  modifiers?: { [K in CSSAnchorModifier]?: CSSAnchorModifierValues[K] | null };
}

type CSSAnchorModifiers = NonNullable<CSSAnchorProps["modifiers"]>;

/**
 * The rendered `data-circle*` attribute bag produced by `cssAnchorProps`.
 *
 * Shared components that receive a pre-built anchor from their caller should
 * type the incoming prop as this and spread it, rather than accepting a raw
 * anchor name — that keeps the anchor literal (and its type check) at the
 * caller. Only `data-circle`-prefixed keys are allowed.
 */
export type CSSAnchorAttributes = Partial<
  Record<"data-circle", CSSAnchor> &
    Record<`data-circle-${CSSAnchorModifier}`, string>
>;

/**
 * Class names targeted by community code snippets.
 *
 * Accepts the same arguments as `classNames`. Returns empty string when
 * V4_CSS is on, acts as a passthrough when off.
 *
 * Will be either removed during V4 cleanup or replaced with a dedicated CSS
 * anchor later.
 */
export const anchorClassNames = (...args: Argument[]): string =>
  window.V4_CSS ? "" : classNames(...args);

/**
 * Internal/structural class names not targeted by community code snippets.
 *
 * Accepts the same arguments as `classNames`. Returns empty string when
 * V4_CSS is on, acts as a passthrough when off.
 *
 * Will be removed during V4 cleanup.
 */
export const legacyClassNames = (...args: Argument[]): string =>
  window.V4_CSS ? "" : classNames(...args);

/**
 * Builds data-circle attributes for CSS customization anchors.
 */
export const cssAnchorProps = ({
  anchor,
  modifiers,
}: CSSAnchorProps): CSSAnchorAttributes => {
  if (!window.V4_UI) return {};

  const entries = Object.entries(modifiers ?? {}) as [
    keyof CSSAnchorModifiers,
    CSSAnchorModifiers[keyof CSSAnchorModifiers],
  ][];

  return entries.reduce<CSSAnchorAttributes>(
    (attributes, [key, value]) =>
      value === false || value === null || value === undefined
        ? attributes
        : {
            ...attributes,
            [`data-circle-${key}`]: value === true ? "" : String(value),
          },
    { "data-circle": anchor },
  );
};
